import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import { cartApi } from '../../services/api'

/**
 * Layout — top nav + content area for all authenticated pages.
 *
 * The nav shows:
 *   - Brand link to dashboard
 *   - Shop / Cart links (for users with a customer_id)
 *   - Reports link (for users with is_gsr or is_area_admin)
 *   - Cart count badge (live; refreshed via 'ghasc:cart-changed' window event)
 *   - User menu with logout
 */
export default function Layout({ children }) {
    const { user, isSysAdmin, logout } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    const hasCustomer = !!user?.customerId
    const hasReporting = !!(user?.isGsr || user?.isAreaAdmin)
    const [cartCount, setCartCount] = useState(0)

    // Load + subscribe to cart updates so the badge stays in sync with
    // adds/removes from anywhere in the app.
    useEffect(() => {
        if (!hasCustomer) return
        refreshCart()

        function onChange() { refreshCart() }
        window.addEventListener('ghasc:cart-changed', onChange)
        return () => window.removeEventListener('ghasc:cart-changed', onChange)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCustomer])

    async function refreshCart() {
        try {
            const response = await cartApi.get()
            if (response.success) setCartCount(response.cart?.totalQuantity || 0)
        } catch { /* non-fatal */ }
    }

    async function handleLogout() {
        await logout()
        toast.success('Logged out')
        navigate('/login')
    }

    function isActive(path) {
        return location.pathname === path
            ? 'text-ghasc-navy font-medium'
            : 'text-gray-600 hover:text-ghasc-navy'
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top nav */}
            <header className="bg-white shadow-sm sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-6">
                    <Link to="/" className="text-xl font-bold text-ghasc-navy whitespace-nowrap">
                        GHASC Literature
                    </Link>

                    <nav className="hidden sm:flex items-center gap-5 text-sm">
                        <Link to="/" className={isActive('/')}>Dashboard</Link>
                        {hasCustomer && (
                            <>
                                <Link to="/shop" className={isActive('/shop')}>Shop</Link>
                                <Link to="/cart" className={`${isActive('/cart')} relative`}>
                                    Cart
                                    {cartCount > 0 && (
                                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium bg-ghasc-tan text-ghasc-navy">
                                            {cartCount}
                                        </span>
                                    )}
                                </Link>
                            </>
                        )}
                        {hasReporting && (
                            <Link to="/reports" className={isActive('/reports')}>Reports</Link>
                        )}
                    </nav>

                    <div className="flex-1" />

                    <div className="flex items-center gap-3 text-sm">
                        <div className="text-right hidden md:block">
                            <div className="text-gray-900 leading-tight">{user?.name}</div>
                            {user?.customerName && (
                                <div className="text-xs text-gray-500 leading-tight">{user.customerName}</div>
                            )}
                        </div>
                        <Link
                            to="/account"
                            className={`text-sm whitespace-nowrap ${location.pathname === '/account'
                                    ? 'text-ghasc-navy font-medium'
                                    : 'text-gray-500 hover:text-ghasc-navy'
                                }`}
                        >
                            My Account
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="text-gray-500 hover:text-ghasc-danger text-sm"
                        >
                            Log out
                        </button>
                    </div>
                </div>

                {/* Mobile nav: shop/cart/reports links inline below the brand */}
                {(hasCustomer || hasReporting) && (
                    <div className="sm:hidden border-t border-gray-100 px-4 py-2 flex gap-4 text-sm">
                        <Link to="/" className={isActive('/')}>Dashboard</Link>
                        {hasCustomer && (
                            <>
                                <Link to="/shop" className={isActive('/shop')}>Shop</Link>
                                <Link to="/cart" className={`${isActive('/cart')} relative`}>
                                    Cart
                                    {cartCount > 0 && (
                                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium bg-ghasc-tan text-ghasc-navy">
                                            {cartCount}
                                        </span>
                                    )}
                                </Link>
                            </>
                        )}
                        {hasReporting && (
                            <Link to="/reports" className={isActive('/reports')}>Reports</Link>
                        )}
                    </div>
                )}
            </header>

            {/* Page content */}
            <main className="max-w-6xl mx-auto px-4 py-6">
                {children}
            </main>
        </div>
    )
}